import React, { useCallback, useMemo, useEffect, useState, SetStateAction, Dispatch, FC, useRef } from 'react';
import _ from 'lodash';
import { Modal, Button } from 'react-bootstrap';

import {
  useInput,
  BasicSelect,
  OptionType,
  ColumnWithSorting,
  Tooltip,
  SelectDropdownInput,
  Loading,
} from '@tradingblock/components';
import {
  CashieringSearchEntityType,
  TransferStates,
  TransferState,
  CashieringBaseEntity,
  AccountStatus,
  AccountStatuses,
} from '@tradingblock/types';
import { useCashieringActions, useCashieringData } from '../useCashiering';
import { Timeframe } from '../../../../components/basic/Timeframe';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { isFetchingCashieringSearchEntities } from '../../../../data/global/selectors/admin/cashieringSelectors';
import { useDispatcher } from '../../../../data/global/hooks';
import dayjs from 'dayjs';
import { exportToCsv, formatMoney } from '../../../../blocks/Analytics/AnalyticsHelper';
import {
  AdminCashieringActions,
  AdminCashieringSearchActions,
} from '../../../../data/global/actions/admin/AdminCashieringActions';
import { useApi } from '../../../../context/Api';

const csvMainActionButtonContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '15px',
  paddingBottom: '15px',
  border: 'white',
  borderColor: 'var(--blue)',
  borderStyle: 'solid',
  borderWidth: '1px',
  marginTop: '20px',
  marginBottom: '20px',
  paddingLeft: '10px',
  paddingRight: '10px',
  borderRadius: '10px',
  width: '100%',
} as object;

const csvMainActionButtonStyle = {
  display: 'flex',
  fontWeight: 'bold',
  alignItems: 'center',
  marginBottom: '5px',
} as object;

const centerStyle = {
  borderRadius: '0px',
  display: 'flex',
  paddingBottom: '1rem',
  flexDirection: 'row',
  justifyContent: 'center',
} as object;

const downloadCSVButtonStyle = {
  width: '25%',
  margin: '0 auto',
  borderRadius: '10px',
} as object;

const checkboxListStyle = {
  padding: '0.3rem',
} as object;

const checkboxStyle = {
  transform: 'scale(1.5, 1.5)',
  marginLeft: 'revert',
  marginRight: '0.8rem',
} as object;

interface CashieringDownloadCSVModalProps {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  cashieringAdminReviewColumns: ColumnWithSorting<CashieringBaseEntity>[];
}

const CashieringDownloadCSVModal: FC<CashieringDownloadCSVModalProps> = ({
  show,
  setShow,
  cashieringAdminReviewColumns,
}) => {
  // Calculate inclusions with checkboxes -> EX:[0, 1] -> exclude table header 1 include table header 2
  // The plus two is to take into account the option to include client and admin notes which do not have their own column in the table.
  // Filter column choices for consistent index references.
  const excludedColumnsById = ['note', 'action', 'filter', 'switch'];

  const [includeList, setIncludeList] = useState<number[]>(
    Array(cashieringAdminReviewColumns.length - excludedColumnsById.length + 2).fill(0)
  );
  const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
  const [errorDownloadingCsv, setErrorDownloadingCsv] = useState<boolean>(false);

  const { data, timeframe, total, ...filterData } = useCashieringData();

  const api = useApi();

  const DISPLAY_NAMES = {
    accountnumber: 'Account Number',
    accountname: 'Account Name',
    repcode: 'Rep code',
    accountstatus: 'Account status',
    transaction: 'Transaction',
    method: 'Method',
    banksignatory: 'Bank Signatory',
    transferamount: 'Transfer Amount',
    transferdirection: 'Transfer Direction',
    date: 'Date',
    status: 'Status',
    latestAdminNote: 'Latest admin note',
    latestClientNote: 'Latest client note',
  };
  const GENERIC_ERROR_DOWNLOADING_CSV_MESSAGE =
    'There was an issue downloading the CSV. Please try downloading the CSV again.';

  const toggleShow = useCallback(
    (shouldShow?: boolean) => {
      const showVal = _.isNil(shouldShow) ? !show : shouldShow;
      setShow(showVal);
    },
    [show]
  );

  const handleHide = useCallback(() => {
    if (!isDownloadingCsv) {
      toggleShow(false);
      setIncludeList(includeList.map((_val: number) => (_val = 0)));
    }
  }, [isDownloadingCsv, includeList]);

  const handleSelectAll = () => setIncludeList(includeList.map((val: number) => (val = 1)));

  // Record all column names
  const columnNames = cashieringAdminReviewColumns
    .filter((col: ColumnWithSorting<CashieringBaseEntity>) => !excludedColumnsById.includes(col.id ? col.id : ''))
    .map((col: ColumnWithSorting<CashieringBaseEntity>) => col.id as string)
    .map(value => {
      if (value === 'AccountName') return DISPLAY_NAMES.accountname;
      else if (value === 'AccountNumber') return DISPLAY_NAMES.accountnumber;
      else if (value === 'RepCode') return DISPLAY_NAMES.repcode;
      else if (value === 'accountStatus') return DISPLAY_NAMES.accountstatus;
      else return value;
    });

  const checkboxSectionLabels = [...columnNames, 'Latest admin note', 'Latest client note'];

  const updateIncludeList = (index: number) =>
    setIncludeList(includeList.toSpliced(index, 1, includeList[index] === 0 ? 1 : 0));

  // Return inputs that will in-place manipulate the final included header list as an encoded boolean array
  const checkboxSections = useMemo(() => {
    return checkboxSectionLabels.map((label: string, index: number) => (
      <div key={index}>
        <div style={checkboxListStyle}>
          <input
            checked={!!includeList[index]}
            onChange={() => updateIncludeList(index)}
            style={checkboxStyle}
            type="checkbox"
          />
          {_.capitalize(label)}
        </div>
        <hr />
      </div>
    ));
  }, [includeList]);

  // clean up for modal
  useEffect(() => {
    return () => {
      setShow(false);
      setIncludeList(includeList.map((_val: number) => (_val = 0)));
    };
  }, []);

  // Export CSV
  const downloadCsv = () => {
    setIsDownloadingCsv(true);
    setErrorDownloadingCsv(false);

    // The plus ten on the page size is to take into account the total number of rows in all the table pages plus those rows that may come in after the user clicks the download button.
    api.cashiering
      .search({
        ...filterData,
        page: 0,
        pageSize: total + 10,
        startDate: timeframe.startDate,
        endDate: timeframe.endDate,
      })
      .then(response => {
        if (response.responseCode === 0 && response.payload.data && response.payload.data.length > 0) {
          try {
            // Map bool array to the cashiering column order
            const selectionList = includeList.map((num, index) => {
              if (num) {
                return checkboxSectionLabels[index].toLowerCase().replaceAll(' ', '');
              }
            });
            const csvData: { [DISPLAY_NAMES: string]: (string | Date)[] } = {
              //to add a column for csv, add here each prop in csvData is a [header name -> column] combination
              //because the data is serialized and chunked later by prop length
              //sequence is the only thing that we will need to calculate
              [DISPLAY_NAMES.accountnumber]: [],
              [DISPLAY_NAMES.accountname]: [],
              [DISPLAY_NAMES.repcode]: [],
              [DISPLAY_NAMES.transaction]: [],
              [DISPLAY_NAMES.status]: [],
              [DISPLAY_NAMES.method]: [],
              [DISPLAY_NAMES.date]: [],
              [DISPLAY_NAMES.accountstatus]: [],
              [DISPLAY_NAMES.banksignatory]: [],
              [DISPLAY_NAMES.transferamount]: [],
              [DISPLAY_NAMES.transferdirection]: [],
              [DISPLAY_NAMES.latestAdminNote]: [],
              [DISPLAY_NAMES.latestClientNote]: [],
            };

            // Build CSV data
            response.payload.data.forEach(row => {
              if (selectionList.find(val => val && val === 'latestclientnote')) {
                if (row.note) {
                  csvData[DISPLAY_NAMES.latestClientNote].push(row.note);
                } else {
                  csvData[DISPLAY_NAMES.latestClientNote].push('NA');
                }
              }

              if (selectionList.find(val => val && val === 'latestadminnote')) {
                if (row.adminNote) {
                  csvData[DISPLAY_NAMES.latestAdminNote].push(row.adminNote);
                } else {
                  csvData[DISPLAY_NAMES.latestAdminNote].push('NA');
                }
              }

              Object.entries(row).map(([key, value]) => {
                const lowerKey = key.toLowerCase();

                if (lowerKey === 'accountnumber' && selectionList.find(val => val && val === 'accountnumber')) {
                  csvData[DISPLAY_NAMES.accountnumber].push(value);
                }

                if (lowerKey === 'accountname' && selectionList.find(val => val && val === 'accountname')) {
                  csvData[DISPLAY_NAMES.accountname].push(value);
                }

                if (lowerKey === 'repcode' && selectionList.find(val => val && val === 'repcode')) {
                  csvData[DISPLAY_NAMES.repcode].push(value);
                }

                if (lowerKey === 'accountstatus' && selectionList.find(val => val && val === 'accountstatus')) {
                  csvData[DISPLAY_NAMES.accountstatus].push(value);
                }

                if (lowerKey === 'date' && selectionList.find(val => val && val === 'date')) {
                  csvData[DISPLAY_NAMES.date].push(new Date(value).toLocaleDateString());
                }

                if (lowerKey === 'type' && selectionList.find(val => val && val === 'transaction')) {
                  csvData[DISPLAY_NAMES.transaction].push(row.type);
                  csvData[DISPLAY_NAMES.status].push(row.status);
                }

                if (selectionList.find(val => val && val === 'method')) {
                  if (lowerKey === 'transfer' && row.transfer) {
                    csvData[DISPLAY_NAMES.method].push('Method: ' + row.transfer.mechanism + ' ' + row.transfer.text);
                    csvData[DISPLAY_NAMES.transferamount].push(formatMoney(row.transfer.amount));
                    csvData[DISPLAY_NAMES.transferdirection].push(
                      row.transfer.direction === 'Outgoing' ? 'WITHDRAWAL' : 'DEPOSIT'
                    );

                    if (row.type === 'AchRelationship') {
                      csvData[DISPLAY_NAMES.banksignatory].push(row.transfer.bankAccountOwnerNameSecret);
                    } else {
                      csvData[DISPLAY_NAMES.banksignatory].push('undefined');
                    }
                  } else if (lowerKey === 'relationship' && row.relationship) {
                    csvData[DISPLAY_NAMES.method].push(
                      'Method: ' + row.relationship.approvalMethod + ' ' + row.relationship.text
                    );
                    csvData[DISPLAY_NAMES.transferamount].push('undefined');
                    csvData[DISPLAY_NAMES.transferdirection].push('undefined');

                    if (row.type === 'AchRelationship') {
                      csvData[DISPLAY_NAMES.banksignatory].push(row.relationship.bankAccountOwnerNameSecret);
                    } else {
                      csvData[DISPLAY_NAMES.banksignatory].push('undefined');
                    }
                  } else if (lowerKey === 'transferwireinstruction' && row.transferWireInstruction) {
                    csvData[DISPLAY_NAMES.method].push('Method: ' + row.transferWireInstruction.text);
                    csvData[DISPLAY_NAMES.transferamount].push('undefined');
                    csvData[DISPLAY_NAMES.transferdirection].push('undefined');
                    csvData[DISPLAY_NAMES.banksignatory].push('undefined');
                  }
                }
              });
            });

            exportToCsv(csvData, undefined, 'cashieringTable');
          } catch {
            setErrorDownloadingCsv(true);
          }
        } else {
          setErrorDownloadingCsv(true);
        }

        setIsDownloadingCsv(false);
      })
      .catch(() => {
        setErrorDownloadingCsv(true);
        setIsDownloadingCsv(false);
      });
  };

  return (
    <>
      <Modal onHide={handleHide} show={show}>
        <Modal.Header closeButton={!isDownloadingCsv}>
          <Modal.Title>CSV download</Modal.Title>
        </Modal.Header>

        <br />

        {checkboxSections}

        {errorDownloadingCsv && <p className="p-1 error">{GENERIC_ERROR_DOWNLOADING_CSV_MESSAGE}</p>}

        <button className="btn btn-dark" style={centerStyle} onClick={handleSelectAll}>
          <span style={downloadCSVButtonStyle}>
            <br />
            <i className="fal fa-check-double fa-x" />
            &nbsp; Select All
          </span>
        </button>

        <button
          className="btn btn-dark"
          style={centerStyle}
          disabled={!includeList.find(val => val !== 0) || isDownloadingCsv}
          onClick={downloadCsv}
        >
          <span style={downloadCSVButtonStyle}>
            <br />
            {isDownloadingCsv ? (
              <Loading />
            ) : (
              <>
                <i className="fal fa-file-excel fa-2x" />
                &nbsp; Download
              </>
            )}
          </span>
        </button>
      </Modal>
    </>
  );
};

// Utility to format transaction type values
function formatTransactionType(type: string) {
  if (type === 'AchRelationship') return 'ACH Relationship';
  if (type === 'TransferWireInstruction') return 'Transfer Wire Instruction';
  if (type === 'AchTransfer') return 'ACH Transfer';
  if (type === 'CheckTransfer') return 'Check Transfer';
  if (type === 'WireTransfer') return 'Wire Transfer';
  // Fallback: insert spaces before capitals
  return type.replace(/([a-z])([A-Z])/g, '$1 $2');
}

// prettier-ignore
export const typeOptions: OptionType<CashieringSearchEntityType | undefined>[] = [
  { value: 'All', label: 'All', data: undefined },
  { value: 'AchRelationship', label: formatTransactionType('AchRelationship'), data: CashieringSearchEntityType.AchRelationship },
  { value: 'Transfer', label: formatTransactionType('Transfer'), data: CashieringSearchEntityType.Transfer },
  { value: 'TransferWireInstruction', label: formatTransactionType('TransferWireInstruction'), data: CashieringSearchEntityType.TransferWireInstruction },
  { value: 'AchTransfer', label: formatTransactionType('AchTransfer'), data: CashieringSearchEntityType.AchTransfer },
  { value: 'CheckTransfer', label: formatTransactionType('CheckTransfer'), data: CashieringSearchEntityType.CheckTransfer },
  { value: 'WireTransfer', label: formatTransactionType('WireTransfer'), data: CashieringSearchEntityType.WireTransfer },
];

// add a space between capitalized words
export const statusOptions: OptionType<TransferState | undefined>[] = _(TransferStates)
  .map((s: TransferState): OptionType<TransferState | undefined> => ({ value: s, label: s, data: s }))
  .union([{ value: 'All', label: 'All', data: undefined }])
  .value()
  .map(o => ({ ...o, label: o.label.replace(/([a-z])([A-Z])/g, '$1 $2') }));

// Place 'All' option at the top of the list
const allOption = statusOptions.find(o => o.value === 'All');
if (allOption) {
  statusOptions.splice(statusOptions.indexOf(allOption), 1);
  statusOptions.unshift(allOption);
}

const accountStatusOptions = AccountStatuses.map(
  (accountStatus: AccountStatus): OptionType<AccountStatus> => ({
    value: accountStatus,
    label: accountStatus.replace(/([a-z])([A-Z])/g, '$1 $2'),
    data: accountStatus,
  })
);

type CashieringFiltersProps = {
  handleSetSelectedRow: (index: number | undefined) => void;
  CashieringAdminReviewColumns: ColumnWithSorting<CashieringBaseEntity>[];
  defaultSearchValue?: string;
};

export const CashieringFilters: React.FC<CashieringFiltersProps> = ({
  handleSetSelectedRow,
  CashieringAdminReviewColumns,
  defaultSearchValue,
}) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const filtersRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const { search, repCode, timeframe, types, status, accountStatus } = useCashieringData();
  const {
    search: onSearch,
    repCode: onRepCodeSearch,
    setTimeframe,
    setType,
    setStatus,
    setAccountStatus,
  } = useCashieringActions();

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (search && search.trim()) count++;
    if (repCode && repCode.trim()) count++;
    if (timeframe.startDate || timeframe.endDate) count++;
    if (types && types.length > 0) count++;
    if (status && status.length > 0) count++;
    if (accountStatus && accountStatus.length > 0) count++;
    return count;
  }, [search, repCode, timeframe, types, status, accountStatus]);
  const [, hasData] = useStateSelector(isFetchingCashieringSearchEntities);

  const [repCodeSearchSubmitted, setRepCodeSearchSubmitted] = useState<boolean>(false);
  const [showCsvDownloadModal, setShowCsvDownloadModal] = useState<boolean>(false);

  // Add state for the search input value
  const [searchInputValue, setSearchInputValue] = useState<string>(defaultSearchValue || '');

  // Load filters from localStorage on mount
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem('cashieringFilters');
      if (savedFilters && !defaultSearchValue) {
        const parsed = JSON.parse(savedFilters);
        if (parsed.search) {
          setSearchInputValue(parsed.search);
          onSearchSubmit(parsed.search);
        }
        if (parsed.repCode) {
          onRepCodeSearchSubmit(parsed.repCode);
        }
        if (parsed.timeframe && parsed.timeframe.startDate && parsed.timeframe && parsed.timeframe.endDate) {
          setTimeframe(new Date(parsed.timeframe.startDate), new Date(parsed.timeframe.endDate));
        }
        if (parsed.types && parsed.types.length > 0) {
          setType(parsed.types);
        }
        if (parsed.status && parsed.status.length > 0) {
          setStatus(parsed.status);
        }
        if (parsed.accountStatus && parsed.accountStatus.length > 0) {
          setAccountStatus(parsed.accountStatus);
        }
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      const filtersToSave = {
        search: search || undefined,
        repCode: repCode || undefined,
        timeframe:
          timeframe.startDate && timeframe.endDate
            ? {
              startDate: timeframe.startDate.toISOString(),
              endDate: timeframe.endDate.toISOString(),
            }
            : undefined,
        types: types || undefined,
        status: status || undefined,
        accountStatus: accountStatus || undefined,
      };
      localStorage.setItem('cashieringFilters', JSON.stringify(filtersToSave));
    } catch (e) {
      console.error('Error saving filters:', e);
    }
  }, [search, repCode, timeframe.startDate, timeframe.endDate, types, status, accountStatus]);


  // When defaultSearchValue changes, update the input value only
  React.useEffect(() => {
    if (defaultSearchValue !== undefined && defaultSearchValue !== searchInputValue) {
      setSearchInputValue(defaultSearchValue);
    }
  }, [defaultSearchValue]);

  // Keep the visible input aligned with Redux when search changes from outside this field.
  // Do not overwrite while the user is typing — otherwise Redux (last submitted value) fights the draft text
  // and the input can collapse to a single character or refuse paste/full account entry.
  React.useEffect(() => {
    if (!search || search === searchInputValue) {
      return;
    }
    if (searchInputRef.current === document.activeElement) {
      return;
    }
    setSearchInputValue(search);
  }, [search, searchInputValue]);

  // When searchInputValue changes (and it's from a programmatic update), trigger the search
  React.useEffect(() => {
    if (defaultSearchValue !== undefined && searchInputValue === defaultSearchValue && searchInputValue.trim() !== '') {
      onSearchSubmit(searchInputValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputValue]);

  const onRepCodeSearchSubmit = useCallback(
    (repCode: string) => {
      if (repCode.trim().length === 0) {
        onRepCodeSearch(undefined);
        setRepCodeSearchSubmitted(false);
      } else {
        onRepCodeSearch(repCode.trim());
        setRepCodeSearchSubmitted(true);
      }
    },
    [repCode]
  );

  const [repCodeValue, setRepCodeValue, repCodeSearchInputProps] = useInput(undefined, {
    onEnter: onRepCodeSearchSubmit,
  });

  const onClearRepCodeSearch = useCallback(() => {
    onRepCodeSearchSubmit('');
    setRepCodeValue('');
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);

  useEffect(() => {
    // if repCode is present but there is no repCodeValue update the repCodeValue
    if (repCode && _.isUndefined(repCodeValue)) {
      setSearchValue(repCode);
    }
    // if repCode is present and repCodeValue is an empty string update the repCodeValue
    if (repCode && repCodeValue === '') {
      setSearchValue(repCode);
    }
  }, [repCode]);

  useEffect(() => {
    // if there is a repCode value but the icon for clearing is missing... (using the clear button's inverted logic)
    if (repCodeValue && (!repCodeSearchSubmitted && !currentRepCode)) {
      // force a repCode
      setRepCodeValue(searchValue.trim());
    }
  }, [repCodeSearchSubmitted]);

  const currentRepCode = useMemo(() => {
    if (!_.isUndefined(repCodeValue)) {
      return repCodeValue;
    }
    return repCode || '';
  }, [repCode, repCodeValue]);

  const [searchSubmitted, setSearchSubmitted] = useState(false);

  const { dispatch } = useDispatcher();

  const onSearchSubmit = useCallback(
    (search: string) => {
      if (search.trim().length === 0) {
        onSearch(undefined);
        setSearchSubmitted(false);
      } else {
        onSearch(search.trim());
        setSearchSubmitted(true);
      }
    },
    [onSearch, dispatch]
  );

  const [searchValue, setSearchValue, inputProps] = useInput(undefined, { onEnter: onSearchSubmit });

  // Sync searchInputValue with searchValue from useInput hook
  React.useEffect(() => {
    if (searchInputValue !== searchValue) {
      setSearchValue(searchInputValue);
    }
  }, [searchInputValue, searchValue]);

  const onClearSearch = useCallback(() => {
    onSearchSubmit('');
    setSearchValue('');
    setSearchInputValue('');
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, [onSearchSubmit, handleSetSelectedRow]);

  useEffect(() => {
    // if there is a search value but the icon for clearing is missing... (using the clear button's inverted logic)
    if (searchValue && (!searchSubmitted && !currentSearch)) {
      // force a search
      setSearchValue(searchValue.trim());
    }
  }, [searchSubmitted]);

  useEffect(() => {
    // if search is present but there is no searchValue update the searchValue
    if (search && _.isUndefined(searchValue)) {
      setSearchValue(search);
    }
    // if search is present and searchValue is an empty string update the searchValue
    if (search && searchValue === '') {
      setSearchValue(search);
    }
  }, [search]);

  const currentSearch = useMemo(() => {
    if (!_.isUndefined(searchValue)) {
      return searchValue;
    }
    return search || '';
  }, [search, searchValue]);

  const currentType = useMemo(() => {
    const firstType = _.first(types);
    if (firstType) {
      return firstType;
    }
    const defaultType = _.first(typeOptions);
    return defaultType ? defaultType.label : '';
  }, [
    types,
    typeOptions,
    currentSearch,
    searchSubmitted,
    isFetchingCashieringSearchEntities,
    searchValue,
    search,
    searchSubmitted,
  ]);

  const currentStatus = useMemo(() => {
    const firstStatus = _.first(status);
    if (firstStatus) {
      // Find the formatted label from statusOptions
      const statusOption = statusOptions.find(opt => opt.value === firstStatus || opt.data === firstStatus);
      return statusOption ? statusOption.label : firstStatus.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    const defaultStatus = _.first(statusOptions);
    return defaultStatus ? defaultStatus.label : '';
  }, [status, statusOptions]);

  const currentStatusValue = useMemo(() => {
    const firstStatus = _.first(status);
    if (firstStatus) {
      return firstStatus;
    }
    const defaultStatus = _.first(statusOptions);
    return defaultStatus ? defaultStatus.value : '';
  }, [status, statusOptions]);

  const onTimeframeChange = useCallback(
    (startDate: Date | undefined, endDate: Date | undefined) => {
      // Validate date range
      if (startDate && endDate && startDate > endDate) {
        setDateValidationError('Start date must be before end date');
        return;
      }
      setDateValidationError(null);

      if (_.isNil(startDate)) {
        setTimeframe(undefined, undefined);
      } else if (_.isNil(endDate)) {
        setTimeframe(startDate, undefined);
      } else {
        setTimeframe(startDate, endDate);
      }
    },
    [setTimeframe]
  );

  // Date range presets
  const applyDatePreset = useCallback(
    (preset: string) => {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      let startDate: Date | undefined;
      let endDate: Date | undefined = today;

      switch (preset) {
        case 'today':
          startDate = new Date();
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'last7days':
          startDate = new Date();
          startDate.setDate(startDate.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'last30days':
          startDate = new Date();
          startDate.setDate(startDate.getDate() - 30);
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'thismonth':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        case 'lastmonth':
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          startDate = lastMonth;
          endDate = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
          break;
        default:
          return;
      }

      setTimeframe(startDate, endDate);
      setDateValidationError(null);
    },
    [setTimeframe]
  );
  const onTypeChange = useCallback((val: OptionType | OptionType[] | null | undefined) => {
    const types = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setType(!types || (types && types[0].data === undefined) ? undefined : [types && types[0].data]);
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);
  const onStatusChange = useCallback((val: OptionType | OptionType[] | null | undefined) => {
    const values = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setStatus(!values || (values && values[0].data === undefined) ? undefined : [values && values[0].data]);
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);

  const [showSearchDate, setShowSearchDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [searchByDateRange, setSearchByDateRange] = useState<boolean>(false);

  const dateRef = useRef([startDate, endDate]);

  //Search total
  useEffect(() => {
    if (startDate && endDate && searchByDateRange) {
      dispatch(AdminCashieringActions.setTimeframe({ startDate: dateRef.current[0], endDate: dateRef.current[1] }));

      setSearchByDateRange(false);
    }
  }, [endDate, startDate, timeframe, dateRef.current, searchByDateRange]);

  const handleDateInput = (event: any) => {
    if (event.target.name === 'start') {
      dateRef.current[0] = event.target.value;
      setStartDate(dateRef.current[0]);
    } else if (event.target.name === 'end') {
      dateRef.current[1] = event.target.value;
      setEndDate(event.target.value);
    }
  };

  const handleReset = (event: any) => {
    if (event.target.name === 'reset') {
      dateRef.current = [undefined, undefined];
      dispatch(
        AdminCashieringActions.setTimeframe({
          startDate: dayjs(new Date().toDateString()).subtract(1, 'week') as any,
          endDate: new Date(),
        })
      );
      dispatch(AdminCashieringSearchActions.setPageSize({ pageSize: 50 }));
    }
  };

  const resetDateButtonStyle = {
    textDecoration: 'underline',
    marginBottom: '20px',
    opacity: !startDate || !endDate ? 0.3 : 1,
    cursor: !startDate || !endDate ? 'not-allowed' : 'inherit',
  } as object;

  const onAccountStatusChange = useCallback((val: AccountStatus[]) => {
    setAccountStatus(!val || (val && val.length === 0) ? undefined : val);
    sessionStorage.removeItem('cashieringSearch');
    handleSetSelectedRow(undefined);
  }, []);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      if (filtersRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = filtersRef.current;
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
        setScrollProgress(Math.min(progress, 100));
      }
    };

    const filtersElement = filtersRef.current;
    if (filtersElement) {
      filtersElement.addEventListener('scroll', handleScroll);
      return () => filtersElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Escape to clear search
      if (event.key === 'Escape' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        if (searchInputRef.current === document.activeElement) {
          onClearSearch();
        }
      }
      // Ctrl/Cmd + K to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current && searchInputRef.current.focus();
      }
      // Ctrl/Cmd + / to show shortcuts
      if ((event.ctrlKey || event.metaKey) && event.key === '/') {
        event.preventDefault();
        setShowShortcuts(!showShortcuts);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClearSearch, showShortcuts]);

  return (
    <div
      ref={filtersRef}
      className="cashiering-filters d-flex flex-direction-col align-items-flex-start"
      style={{
        background: 'var(--blue-dark-lighter)',
        border: '1px solid var(--lighten)',
        borderRadius: 'var(--radius)',
        padding: 'var(--gap)',
        paddingBottom: 'calc(var(--gap) + 60px)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
        marginBottom: 0,
        position: 'relative',
        height: '100%',
        minHeight: 0,
        flex: '1 1 auto',
        scrollBehavior: 'smooth',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Scroll progress indicator */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          width: `${scrollProgress}%`,
          background: 'var(--teal)',
          transition: 'width 0.1s ease-out',
          zIndex: 10,
        }}
      />

      {/* Search inputs - always visible */}
      <div className="mb-20px d-flex flex-direction-col w-100">
        {/* Clickable Filter Chips */}
        {activeFilterCount > 0 && (
          <div
            style={{
              background: 'var(--blue-dark-lighter)',
              borderRadius: 'var(--radius)',
              padding: '8px 12px',
              marginBottom: '12px',
              border: '1px solid var(--lighten)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
              <i className="fal fa-filter" style={{ fontSize: '0.9rem', color: 'var(--teal)' }}></i>
              <strong style={{ color: 'var(--teal)', marginRight: '4px' }}>
                {activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''}:
              </strong>
              {search && (
                <span
                  onClick={() => onClearSearch()}
                  style={{
                    background: 'var(--blue-dark)',
                    border: '1px solid var(--teal)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = 'var(--blue-dark)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--blue-dark)';
                    e.currentTarget.style.color = 'var(--teal)';
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove search filter: ${search}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClearSearch();
                    }
                  }}
                >
                  Search: "{search}" <i className="fal fa-times" style={{ fontSize: '0.7rem' }}></i>
                </span>
              )}
              {repCode && (
                <span
                  onClick={() => onClearRepCodeSearch()}
                  style={{
                    background: 'var(--blue-dark)',
                    border: '1px solid var(--teal)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = 'var(--blue-dark)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--blue-dark)';
                    e.currentTarget.style.color = 'var(--teal)';
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Remove rep code filter: ${repCode}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onClearRepCodeSearch();
                    }
                  }}
                >
                  Rep: "{repCode}" <i className="fal fa-times" style={{ fontSize: '0.7rem' }}></i>
                </span>
              )}
              {(timeframe.startDate || timeframe.endDate) && (
                <span
                  onClick={() => setTimeframe(undefined, undefined)}
                  style={{
                    background: 'var(--blue-dark)',
                    border: '1px solid var(--teal)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = 'var(--blue-dark)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--blue-dark)';
                    e.currentTarget.style.color = 'var(--teal)';
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Remove date range filter"
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setTimeframe(undefined, undefined);
                    }
                  }}
                >
                  Date Range <i className="fal fa-times" style={{ fontSize: '0.7rem' }}></i>
                </span>
              )}
              {types && types.length > 0 && (
                <span
                  onClick={() => setType(undefined)}
                  style={{
                    background: 'var(--blue-dark)',
                    border: '1px solid var(--teal)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = 'var(--blue-dark)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--blue-dark)';
                    e.currentTarget.style.color = 'var(--teal)';
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Remove transaction type filter"
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setType(undefined);
                    }
                  }}
                >
                  Type: {types.length} selected <i className="fal fa-times" style={{ fontSize: '0.7rem' }}></i>
                </span>
              )}
              {status && status.length > 0 && (
                <span
                  onClick={() => setStatus(undefined)}
                  style={{
                    background: 'var(--blue-dark)',
                    border: '1px solid var(--teal)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = 'var(--blue-dark)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--blue-dark)';
                    e.currentTarget.style.color = 'var(--teal)';
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Remove transaction status filter"
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setStatus(undefined);
                    }
                  }}
                >
                  Status: {status.length} selected <i className="fal fa-times" style={{ fontSize: '0.7rem' }}></i>
                </span>
              )}
              {accountStatus && accountStatus.length > 0 && (
                <span
                  onClick={() => setAccountStatus(undefined)}
                  style={{
                    background: 'var(--blue-dark)',
                    border: '1px solid var(--teal)',
                    borderRadius: 'var(--radius)',
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    color: 'var(--teal)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--teal)';
                    e.currentTarget.style.color = 'var(--blue-dark)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--blue-dark)';
                    e.currentTarget.style.color = 'var(--teal)';
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Remove account status filter"
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAccountStatus(undefined);
                    }
                  }}
                >
                  Account: {accountStatus.length} selected{' '}
                  <i className="fal fa-times" style={{ fontSize: '0.7rem' }}></i>
                </span>
              )}
            </div>
          </div>
        )}
        <label
          htmlFor="cashiering-admin-search-input"
          style={{
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
            marginBottom: '0.35rem',
            display: 'block',
            letterSpacing: '0.02em',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
          }}
        >
          Account / notes
        </label>
        <div className="d-flex w-100 align-items-center mb-20px cashiering-filters-tooltip-wrapper">
          <Tooltip
            content="You can search for account number, account title, account name, or notes. Press Ctrl+K to focus."
            id="search-tooltip"
            effect="solid"
            place="top"
          >
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                id="cashiering-admin-search-input"
                ref={searchInputRef}
                className="input input-dark input-tall w-100"
                type="text"
                placeholder={'Search... (Ctrl+K to focus)'}
                value={searchInputValue}
                onChange={e => {
                  const newValue = e.target.value;
                  setSearchInputValue(newValue);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onSearchSubmit(e.currentTarget.value);
                  }
                }}
                style={{
                  border: 'var(--field-border)',
                  borderRadius: 'var(--radius)',
                  backgroundColor: 'var(--blue-dark)',
                  color: '#fff',
                  padding: '12px 16px',
                  paddingRight: '40px',
                  fontSize: '1rem',
                  transition: 'all 0.2s ease-in-out',
                }}
                aria-label="Search cashiering records"
                aria-describedby="search-tooltip"
              />
              {isLoading ? (
                <div
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }}
                >
                  <Loading />
                </div>
              ) : (
                <i
                  className="fal fa-search"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--teal)',
                    fontSize: '1.1rem',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </Tooltip>
          {searchInputValue && (
            <i
              className="fa fa-times fa-lg"
              onClick={onClearSearch}
              style={{ color: 'var(--teal)', cursor: 'pointer', paddingLeft: '0.5rem', flexShrink: 0 }}
              aria-label="Clear search"
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClearSearch();
                }
              }}
            />
          )}
        </div>
        <label
          htmlFor="cashiering-admin-rep-code"
          style={{
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.875rem',
            marginTop: '0.25rem',
            marginBottom: '0.35rem',
            display: 'block',
            letterSpacing: '0.02em',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
          }}
        >
          Rep code
        </label>
        <div className="d-flex w-100 align-items-center">
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              className="input input-dark input-tall w-100"
              type="text"
              placeholder={'Search by rep code...'}
              value={currentRepCode}
              {...repCodeSearchInputProps}
              id="cashiering-admin-rep-code"
              style={{
                border: 'var(--field-border)',
                borderRadius: 'var(--radius)',
                backgroundColor: 'var(--blue-dark)',
                color: '#fff',
                padding: '12px 16px',
                paddingRight: '40px',
                fontSize: '1rem',
                transition: 'all 0.2s ease-in-out',
              }}
            />
            <i
              className="fal fa-user-tie"
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--teal)',
                fontSize: '1.1rem',
                pointerEvents: 'none',
              }}
            />
          </div>
          {currentRepCode && (
            <i
              className="fa fa-times fa-lg"
              onClick={onClearRepCodeSearch}
              style={{ color: 'var(--teal)', cursor: 'pointer', paddingLeft: '0.5rem', flexShrink: 0 }}
              aria-label="Clear rep code search"
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClearRepCodeSearch();
                }
              }}
            />
          )}
        </div>
      </div>
      <div
        className="d-flex flex-direction-col align-items-flex-start w-100"
        style={{
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Section Header */}
        <div
          style={{
            background: 'var(--blue-dark-lighter)',
            padding: '8px 16px',
            marginBottom: '20px',
            borderRadius: 'var(--radius)',
            borderLeft: '3px solid var(--teal)',
          }}
        >
          <h3
            style={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '1.1rem',
              margin: 0,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-sliders-h" style={{ marginRight: '8px' }}></i>
            Filter Options
          </h3>
        </div>

        <div className="mb-20px w-100">
          <label
            htmlFor="cashiering-filters-timeframe"
            style={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: '0.5rem',
              display: 'block',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-calendar-alt" style={{ marginRight: '8px' }}></i>
            Date Range
          </label>
          {/* Date Range Presets */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            {[
              { key: 'today', label: 'Today' },
              { key: 'last7days', label: 'Last 7 Days' },
              { key: 'last30days', label: 'Last 30 Days' },
              { key: 'thismonth', label: 'This Month' },
              { key: 'lastmonth', label: 'Last Month' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => applyDatePreset(key)}
                style={{
                  background: 'var(--blue-dark-lighter)',
                  border: '1px solid var(--lighten)',
                  borderRadius: 'var(--radius)',
                  padding: '4px 8px',
                  fontSize: '0.7rem',
                  color: 'var(--teal)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--teal)';
                  e.currentTarget.style.color = 'var(--blue-dark)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'var(--blue-dark-lighter)';
                  e.currentTarget.style.color = 'var(--teal)';
                }}
                aria-label={`Set date range to ${label.toLowerCase()}`}
              >
                {label}
              </button>
            ))}
          </div>
          <Timeframe
            initialStartDate={timeframe.startDate}
            initialEndDate={timeframe.endDate}
            btnClassName="btn-dark btn-tall dropdown-toggle text-left position-relative w-100"
            placeholderClassName="select__placeholder"
            caret={<>&nbsp;&nbsp;</>}
            onSave={onTimeframeChange}
            buttonId="cashiering-filters-timeframe"
            aria-label="Select date range"
          />
          {dateValidationError && (
            <div
              style={{
                color: 'var(--salmon)',
                fontSize: '0.75rem',
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
              role="alert"
              aria-live="polite"
            >
              <i className="fal fa-exclamation-circle"></i>
              {dateValidationError}
            </div>
          )}
        </div>

        <div
          className="d-flex mb-3"
          style={{
            alignItems: 'center',
            background: 'var(--blue-dark-lighter)',
            padding: '8px 12px',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--lighten)',
          }}
        >
          <input
            checked={!!showSearchDate}
            onChange={() => setShowSearchDate(!showSearchDate)}
            style={{ ...checkboxStyle }}
            type="checkbox"
          />
          <strong
            style={{
              color: '#fff',
              marginLeft: '0.5rem',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-search" style={{ marginRight: '8px' }}></i>
            Search by date
          </strong>
        </div>
        {showSearchDate && (
          <span>
            <span>
              <input
                style={{
                  color: '#fff',
                  backgroundColor: 'var(--blue-dark)',
                  border: 'var(--field-border)',
                  marginBottom: '10px',
                  marginTop: '10px',
                }}
                className="input input-tall"
                value={dateRef.current[0] ? (dateRef.current[0] as Date) : ('' as any)}
                onChange={e => handleDateInput(e)}
                id="csv_StartDate"
                name="start"
                type="date"
                required
              />
              &nbsp; <span style={{ color: '#fff' }}>Start Date</span>
            </span>
            <span>
              <br />
              <input
                style={{
                  color: '#fff',
                  backgroundColor: 'var(--blue-dark)',
                  border: 'var(--field-border)',
                }}
                className="input input-tall"
                value={dateRef.current[1] ? (dateRef.current[1] as Date) : ('' as any)}
                onChange={e => handleDateInput(e)}
                id="csv_EndDate"
                name="end"
                type="date"
                required
              />
              &nbsp; <span style={{ color: '#fff' }}>End Date</span>
            </span>
            &nbsp;
            <br />
            <Button
              onClick={() => setSearchByDateRange(true)}
              style={{
                width: 'max-content',
                marginTop: '10px',
                backgroundColor: 'var(--teal)',
                borderColor: 'var(--teal)',
                color: '#fff',
                borderRadius: 'var(--radius)',
              }}
              variant="primary"
            >
              Search
            </Button>
            <hr style={{ borderColor: 'var(--lighten)' }} />
          </span>
        )}
        <button
          disabled={!startDate || !endDate}
          style={{
            ...resetDateButtonStyle,
            color: 'var(--teal)',
            textDecoration: 'underline',
            border: 'none',
            background: 'none',
            padding: '0.5rem 0',
          }}
          onClick={e => handleReset(e)}
          id="reset"
          name="reset"
        >
          Reset date search
        </button>

        <div className="w-100 mb-20px cashiering-filters-select-dropdown-input">
          <label
            style={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: '0.5rem',
              display: 'block',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-user-circle" style={{ marginRight: '8px' }}></i>
            Account Status
          </label>
          <SelectDropdownInput
            isMulti
            options={accountStatusOptions}
            placeholder="Account Status"
            name="account-status-select-input"
            value={accountStatus || []}
            onchange={(_e, value) => onAccountStatusChange(value)}
          />
        </div>

        <div className="mb-20px w-100 cashiering-filters-filter-dropdown">
          <label
            htmlFor="transaction-type-select-input"
            style={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: '0.5rem',
              display: 'block',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-credit-card" style={{ marginRight: '8px' }}></i>
            Transaction Type
          </label>
          <BasicSelect
            onChange={onTypeChange}
            placeholder={formatTransactionType(currentType)}
            className="btn btn-dark btn-tall dropdown-toggle text-left w-100 p-0"
            options={typeOptions}
            isClearable={false}
            value={
              typeOptions.find(opt => opt.value === currentType) || {
                value: currentType,
                label: formatTransactionType(currentType),
              }
            }
            inputId="transaction-type-select-input"
            style={{
              border: 'var(--field-border)',
              backgroundColor: 'var(--blue-dark)',
            }}
          />
        </div>

        <div className="cashiering-filters-filter-dropdown w-100 mb-20px">
          <label
            htmlFor="transaction-status-select-input"
            style={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '1rem',
              marginBottom: '0.5rem',
              display: 'block',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-info-circle" style={{ marginRight: '8px' }}></i>
            Transaction Status
          </label>
          <BasicSelect
            onChange={onStatusChange}
            placeholder={currentStatus}
            className="btn btn-dark btn-tall dropdown-toggle text-left w-100 p-0"
            options={statusOptions}
            isClearable={false}
            value={
              statusOptions.find(opt => opt.value === currentStatusValue || opt.data === currentStatusValue) || {
                value: currentStatusValue,
                label: currentStatus,
              }
            }
            inputId="transaction-status-select-input"
            style={{
              border: 'var(--field-border)',
              backgroundColor: 'var(--blue-dark)',
            }}
          />
        </div>

        {/* Quick Actions Section */}
        <div
          style={{
            background: 'var(--blue-dark-lighter)',
            borderRadius: 'var(--radius)',
            padding: '12px',
            marginBottom: '20px',
            border: '1px solid var(--lighten)',
          }}
        >
          <div
            style={{
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.9rem',
              marginBottom: '8px',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            }}
          >
            <i className="fal fa-bolt" style={{ marginRight: '8px' }}></i>
            Quick Actions
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                // Clear all search and filters
                onSearchSubmit('');
                setSearchValue('');
                setSearchInputValue('');
                onRepCodeSearchSubmit('');
                setRepCodeValue('');
                setTimeframe(undefined, undefined);
                setType(undefined);
                setStatus(undefined);
                setAccountStatus(undefined);
                sessionStorage.removeItem('cashieringSearch');
                handleSetSelectedRow(undefined);

                // Visual feedback
                const button = event.currentTarget;
                if (button) {
                  button.style.transform = 'scale(0.95)';
                  setTimeout(() => {
                    if (button) {
                      button.style.transform = 'scale(1)';
                    }
                  }, 150);
                }
              }}
              style={{
                background: 'var(--blue-dark-lighter)',
                border: '1px solid var(--salmon)',
                borderRadius: 'var(--radius)',
                padding: '6px 12px',
                color: 'var(--salmon)',
                cursor: 'pointer',
                fontSize: '0.8rem',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--salmon)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--blue-dark-lighter)';
                e.currentTarget.style.color = 'var(--salmon)';
              }}
            >
              <i className="fal fa-trash-alt" style={{ marginRight: '6px' }}></i>
              Clear All
            </button>
            <button
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                // Reset only filter dropdowns, keep search
                setTimeframe(undefined, undefined);
                setType(undefined);
                setStatus(undefined);
                setAccountStatus(undefined);
                sessionStorage.removeItem('cashieringSearch');
                handleSetSelectedRow(undefined);

                // Visual feedback
                const button = event.currentTarget;
                if (button) {
                  button.style.transform = 'scale(0.95)';
                  setTimeout(() => {
                    if (button) {
                      button.style.transform = 'scale(1)';
                    }
                  }, 150);
                }
              }}
              style={{
                background: 'var(--blue-dark-lighter)',
                border: '1px solid var(--gold)',
                borderRadius: 'var(--radius)',
                padding: '6px 12px',
                color: 'var(--gold)',
                cursor: 'pointer',
                fontSize: '0.8rem',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--gold)';
                e.currentTarget.style.color = 'var(--blue-dark)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--blue-dark-lighter)';
                e.currentTarget.style.color = 'var(--gold)';
              }}
            >
              <i className="fal fa-redo" style={{ marginRight: '6px' }}></i>
              Reset Filters
            </button>
            <button
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                // Show only pending approvals
                setStatus(['PendingFirmCreateApproval', 'PendingRepCreateApproval']);
                setType(undefined);
                setTimeframe(undefined, undefined);
                setAccountStatus(undefined);
                sessionStorage.removeItem('cashieringSearch');
                handleSetSelectedRow(undefined);

                // Visual feedback
                const button = event.currentTarget;
                if (button) {
                  button.style.transform = 'scale(0.95)';
                  setTimeout(() => {
                    if (button) {
                      button.style.transform = 'scale(1)';
                    }
                  }, 150);
                }
              }}
              style={{
                background: 'var(--blue-dark-lighter)',
                border: '1px solid var(--teal)',
                borderRadius: 'var(--radius)',
                padding: '6px 12px',
                color: 'var(--teal)',
                cursor: 'pointer',
                fontSize: '0.8rem',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--teal)';
                e.currentTarget.style.color = 'var(--blue-dark)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--blue-dark-lighter)';
                e.currentTarget.style.color = 'var(--teal)';
              }}
            >
              <i className="fal fa-clock" style={{ marginRight: '6px' }}></i>
              Pending Only
            </button>
          </div>
        </div>

        {hasData && (
          <div
            style={{
              background: 'var(--blue-dark-lighter)',
              borderRadius: 'var(--radius)',
              padding: '2px',
              marginTop: '20px',
            }}
          >
            <button
              onClick={() => setShowCsvDownloadModal(true)}
              style={{
                ...csvMainActionButtonContainerStyle,
                border: 'none',
                backgroundColor: 'var(--blue-dark)',
                color: 'var(--teal)',
                transition: 'all 0.3s ease-in-out',
                borderRadius: 'var(--radius)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(25, 186, 180, 0.2)';
                e.currentTarget.style.color = 'var(--teal-light)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.color = 'var(--teal)';
              }}
            >
              <span
                style={{
                  ...csvMainActionButtonStyle,
                  color: 'var(--teal)',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                }}
              >
                <i className="fal fa-file-excel" style={{ marginRight: '8px' }}></i>
                CSV Download
              </span>
            </button>
          </div>
        )}
        {showCsvDownloadModal && (
          <CashieringDownloadCSVModal
            show={showCsvDownloadModal}
            setShow={setShowCsvDownloadModal}
            cashieringAdminReviewColumns={CashieringAdminReviewColumns}
          />
        )}
      </div>

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={() => setShowShortcuts(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="shortcuts-title"
        >
          <div
            style={{
              background: 'var(--blue-dark)',
              border: '1px solid var(--lighten)',
              borderRadius: 'var(--radius)',
              padding: 'var(--gap)',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}
            >
              <h3 id="shortcuts-title" style={{ color: '#fff', margin: 0 }}>
                <i className="fal fa-keyboard" style={{ marginRight: '8px' }}></i>
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setShowShortcuts(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--teal)',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                }}
                aria-label="Close shortcuts"
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#fff' }}>Focus search</span>
                <kbd
                  style={{
                    background: 'var(--blue-dark-lighter)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    color: 'var(--teal)',
                  }}
                >
                  Ctrl+K
                </kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#fff' }}>Show shortcuts</span>
                <kbd
                  style={{
                    background: 'var(--blue-dark-lighter)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    color: 'var(--teal)',
                  }}
                >
                  Ctrl+/
                </kbd>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#fff' }}>Clear search</span>
                <kbd
                  style={{
                    background: 'var(--blue-dark-lighter)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    color: 'var(--teal)',
                  }}
                >
                  Esc
                </kbd>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer to push content to top */}
      <div style={{ flex: 1 }} />

      {/* Quick Actions Bar for Collapsed State */}
      {/* Removed collapse-related state and functionality */}

      {/* Back to Top Button */}
      {scrollProgress > 20 && (
        <button
          onClick={() => {
            if (filtersRef.current) {
              filtersRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            background: 'var(--blue-dark-lighter)',
            border: '1px solid var(--teal)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            color: 'var(--teal)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            transition: 'all 0.2s ease',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--teal)';
            e.currentTarget.style.color = 'var(--blue-dark)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'var(--blue-dark-lighter)';
            e.currentTarget.style.color = 'var(--teal)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ↑
        </button>
      )}
    </div>
  );
};
